import React from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import classNames from 'classnames';
import {
  ArrowRight,
  Book,
  BookOpen,
  Bookmark,
  CalendarDays,
  CalendarHeart,
  CalendarRange,
  Frame,
  Gift,
  GitFork,
  Image as ImageIcon,
  Info,
  Layers,
  Leaf,
  MapPin,
  MonitorSmartphone,
  Network,
  Package,
  Palette,
  Plus,
  Ruler,
  Scissors,
  Sparkles,
  TreeDeciduous,
  UserRound,
  UserRoundPlus,
} from 'lucide-react';
import { CloudinaryPicture, HtmlContent, WIDTH_LADDERS } from 'components/rich';
import { formatPrice } from 'utils/format';
import productCoverImage from 'utils/productCoverImage';

import FaqAccordion from '../faq/faqAccordion';
import { ProductDetailLink } from '../pageLinks';
import { SUPPORT_EMAIL } from '../support/copyEmailButton';
import styles from './product.module.scss';

const IMAGE_PREFIX = 'products/';
const IMAGE_WIDTHS = WIDTH_LADDERS.products;

// SPEC_ICONS in packages/server/src/lib/productContentSchema.js
const ICONS = {
  package: Package,
  ruler: Ruler,
  plus: Plus,
  leaf: Leaf,
  frame: Frame,
  image: ImageIcon,
  mapPin: MapPin,
  info: Info,
  network: Network,
  bookOpen: BookOpen,
  book: Book,
  bookmark: Bookmark,
  layers: Layers,
  sparkles: Sparkles,
  gitFork: GitFork,
  scissors: Scissors,
  palette: Palette,
  userRoundPlus: UserRoundPlus,
  calendarHeart: CalendarHeart,
  calendarRange: CalendarRange,
  calendarDays: CalendarDays,
  gift: Gift,
  monitorSmartphone: MonitorSmartphone,
};

export const Icon = ({ name, size }) => {
  const Component = ICONS[name];
  return Component ? <Component size={size} aria-hidden="true" /> : null;
};

Icon.propTypes = {
  name: PropTypes.string,
  size: PropTypes.number.isRequired,
};

// Quotes longer than this get the smaller quote size, so the band keeps its height
const LONG_QUOTE_CHARS = 80;

export const SECTION_IDS = {
  story: 'story',
  details: 'details',
  testimonial: 'reviews',
  fact: 'fact',
  faq: 'faq',
  related: 'related',
};

const SectionHead = ({ eyebrow, title, as: Heading = 'h2', className }) => (
  <div className={classNames(styles.sectionHead, className)}>
    {eyebrow && <p className={styles.eyebrow}>{eyebrow}</p>}
    <Heading className={styles.sectionTitle}>{title}</Heading>
  </div>
);

SectionHead.propTypes = {
  eyebrow: PropTypes.string,
  title: PropTypes.string.isRequired,
  as: PropTypes.string,
  className: PropTypes.string,
};

export const ProductStory = ({ story }) => (
  <section id={SECTION_IDS.story} className={styles.story}>
    <div className={styles.container}>
      <div className={styles.storyHead}>
        <SectionHead eyebrow={story.eyebrow} title={story.title} />
        {story.intro && <p className={styles.storyIntro}>{story.intro}</p>}
      </div>

      <ol className={styles.chapters}>
        {story.chapters.map((chapter, index) => (
          <li
            key={chapter.title}
            className={classNames(styles.chapter, { [styles.chapterFlip]: index % 2 === 1 })}
          >
            <div className={styles.chapterPhoto}>
              <CloudinaryPicture
                prefix={IMAGE_PREFIX}
                imageKey={chapter.imageKey}
                widths={IMAGE_WIDTHS}
                sizes="(min-width: 992px) 570px, calc(100vw - 88px)"
                loading="lazy"
              />
            </div>
            <div className={styles.chapterMarker} aria-hidden="true">
              <span className={styles.chapterNode}>{index + 1}</span>
            </div>
            <p className={classNames(styles.eyebrow, styles.chapterEyebrow)}>{chapter.eyebrow}</p>
            <h3 className={styles.chapterTitle}>{chapter.title}</h3>
            <p className={styles.chapterBody}>{chapter.body}</p>
            {chapter.tags && (
              <ul className={styles.tags}>
                {chapter.tags.map((tag) => (
                  <li key={tag}>{tag}</li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ol>
    </div>
  </section>
);

ProductStory.propTypes = {
  story: PropTypes.object.isRequired,
};

// Five week rows of the month page in the hanging calendar drawing
const CALENDAR_DAYS = 35;

const SizeShape = ({ shape, size }) => {
  const style = { '--w': size.width, '--h': size.height };

  if (shape === 'book') {
    return (
      <span className={styles.drawingBook} style={style} aria-hidden="true">
        Our family tree
      </span>
    );
  }

  // A calendar size taller than wide is the open calendar on the wall: photo page above
  // the binding, month page below. Otherwise it is drawn closed, cover up.
  if (shape === 'calendar' && size.height > size.width) {
    return (
      <span className={styles.drawingCalendar} style={style} aria-hidden="true">
        <span className={styles.calendarPage}>
          <span className={styles.calendarPhoto} />
        </span>
        <span className={styles.calendarRing} />
        <span className={styles.calendarPage}>
          <span className={styles.calendarMonth}>April</span>
          <span className={styles.calendarWeekdays} />
          <span className={styles.calendarGrid}>
            {Array.from({ length: CALENDAR_DAYS }, (_, day) => (
              <span key={day} />
            ))}
          </span>
        </span>
      </span>
    );
  }

  if (shape === 'calendar') {
    return (
      <span className={styles.drawingCalendar} style={style} aria-hidden="true">
        <span className={styles.calendarRing} />
        <span className={styles.calendarCover}>
          <span className={styles.calendarPhoto} />
          <span className={styles.calendarTitle}>Our family calendar</span>
        </span>
      </span>
    );
  }

  return (
    <span className={styles.drawingPoster} style={style} aria-hidden="true">
      <span className={styles.drawingRail} />
      <span className={styles.drawingRail} />
    </span>
  );
};

SizeShape.propTypes = {
  shape: PropTypes.string.isRequired,
  size: PropTypes.object.isRequired,
};

// Back and front of one card size, the sample name only shows the caption layout
const CardSides = ({ size }) => {
  const style = { '--w': size.width, '--h': size.height };

  return (
    <ul className={classNames(styles.drawing, styles['drawing-card'])}>
      <li className={styles.drawingItem}>
        <span className={styles.drawingCard} style={style} aria-hidden="true">
          <TreeDeciduous className={styles.cardTree} />
        </span>
        <span className={styles.drawingLabel}>Back</span>
      </li>
      <li className={styles.drawingItem}>
        <span className={styles.drawingCard} style={style} aria-hidden="true">
          <span className={styles.cardPhoto}>
            <UserRound className={styles.cardPerson} />
          </span>
          <span className={styles.cardCaption}>
            <span className={styles.cardName}>Grace Kent</span>
            <span className={styles.cardYears}>1994</span>
          </span>
        </span>
        <span className={styles.drawingLabel}>Front</span>
      </li>
    </ul>
  );
};

CardSides.propTypes = {
  size: PropTypes.object.isRequired,
};

const SizeDrawing = ({ drawing, sizes }) => (
  <div className={styles.sizesCard}>
    <div className={styles.sizesHead}>
      <h3 className={styles.cardTitle}>{drawing.title}</h3>
      {drawing.intro && <p className={styles.cardText}>{drawing.intro}</p>}
    </div>
    {drawing.shape === 'card' ? (
      <CardSides size={sizes[0]} />
    ) : (
      <ul className={classNames(styles.drawing, styles[`drawing-${drawing.shape}`])}>
        {sizes.map((size) => (
          <li key={size.label} className={styles.drawingItem}>
            <SizeShape shape={drawing.shape} size={size} />
            <span className={styles.drawingLabel}>{size.label}</span>
            <span className={styles.drawingCm}>{size.cm}</span>
            {size.use && (
              <span className={classNames(styles.pill, { [styles.pillAccent]: size.highlight })}>
                {size.use}
              </span>
            )}
          </li>
        ))}
      </ul>
    )}
  </div>
);

SizeDrawing.propTypes = {
  drawing: PropTypes.object.isRequired,
  sizes: PropTypes.array.isRequired,
};

const OptionCards = ({ optionCards }) => (
  <div className={styles.optionCards}>
    <div className={styles.optionCardsHead}>
      <h3 className={styles.subsectionTitle}>{optionCards.title}</h3>
      {optionCards.note && <p className={styles.optionCardsNote}>{optionCards.note}</p>}
    </div>
    <ul className={styles.cardRow} style={{ '--columns': optionCards.items.length }}>
      {optionCards.items.map((item) => (
        <li key={item.title} className={styles.optionCard}>
          {item.imageKey ? (
            <div className={styles.optionCardPhoto}>
              <CloudinaryPicture
                prefix={IMAGE_PREFIX}
                imageKey={item.imageKey}
                widths={IMAGE_WIDTHS}
                sizes="(min-width: 992px) 404px, 260px"
                loading="lazy"
              />
            </div>
          ) : (
            <div className={styles.optionCardDiagram}>
              {item.image && <img src={item.image} alt="" loading="lazy" />}
            </div>
          )}
          <div className={styles.cardBody}>
            <h4 className={styles.cardTitle}>{item.title}</h4>
            <p className={styles.cardText}>{item.description}</p>
            {item.badge && <span className={styles.pill}>{item.badge}</span>}
          </div>
        </li>
      ))}
    </ul>
  </div>
);

OptionCards.propTypes = {
  optionCards: PropTypes.object.isRequired,
};

const HowToPlay = ({ howToPlay }) => (
  <div className={styles.howToPlay}>
    <div className={styles.howToPlayHead}>
      <h3 className={styles.subsectionTitle}>{howToPlay.title}</h3>
      {howToPlay.note && <p className={styles.optionCardsNote}>{howToPlay.note}</p>}
    </div>
    <ol className={styles.steps}>
      {howToPlay.steps.map((step, index) => (
        <li key={step.title} className={styles.step}>
          <div className={styles.stepArt}>
            <img src={step.image} alt="" loading="lazy" />
          </div>
          <div className={styles.stepBody}>
            <p className={styles.eyebrow}>Step {index + 1}</p>
            <h4 className={styles.stepTitle}>{step.title}</h4>
            <p className={styles.stepText}>{step.body}</p>
          </div>
        </li>
      ))}
    </ol>
  </div>
);

HowToPlay.propTypes = {
  howToPlay: PropTypes.object.isRequired,
};

export const ProductDetails = ({ content }) => (
  <section id={SECTION_IDS.details} className={styles.details}>
    <div className={styles.container}>
      <SectionHead eyebrow="Product details" title={content.detailsTitle ?? 'Product details'} />

      <div className={styles.detailsRow}>
        <dl className={styles.specTable}>
          {content.specs.map((spec) => (
            <div key={spec.label} className={styles.specRow}>
              <dt>
                <Icon name={spec.icon} size={22} />
                {spec.label}
              </dt>
              <dd>{spec.value}</dd>
            </div>
          ))}
        </dl>

        {content.sizes && content.sizeDrawing && (
          <SizeDrawing drawing={content.sizeDrawing} sizes={content.sizes} />
        )}
      </div>

      {content.optionCards && <OptionCards optionCards={content.optionCards} />}
      {content.howToPlay && <HowToPlay howToPlay={content.howToPlay} />}
    </div>
  </section>
);

ProductDetails.propTypes = {
  content: PropTypes.object.isRequired,
};

export const PairCards = ({ cards, getProduct }) => {
  const items = cards
    .map((card) => ({ ...card, linked: getProduct(card.product) }))
    .filter(({ linked }) => linked);

  if (items.length === 0) return null;

  return (
    <section className={styles.pairs}>
      <div className={styles.container}>
        <h2 className={styles.subsectionTitle}>Pairs well with</h2>
        <ul className={styles.pairRow}>
          {items.map(({ product, title, body, imageKey, icon, linkLabel, linked }) => (
            <li
              key={product}
              className={classNames(styles.pairCard, { [styles.pairNote]: !imageKey })}
            >
              {imageKey ? (
                <div className={styles.pairPhoto}>
                  <CloudinaryPicture
                    prefix={IMAGE_PREFIX}
                    imageKey={imageKey}
                    widths={IMAGE_WIDTHS}
                    sizes="(min-width: 992px) 260px, calc(100vw - 32px)"
                    loading="lazy"
                  />
                </div>
              ) : (
                <span className={styles.pairIcon}>
                  <Icon name={icon} size={28} />
                </span>
              )}
              <div className={styles.pairBody}>
                <h3 className={styles.cardTitle}>{title ?? linked.name}</h3>
                <p className={styles.pairText}>{body}</p>
                <ProductDetailLink productId={linked.id}>
                  <a className={styles.arrowLink}>
                    {linkLabel ?? `View ${linked.name}`}
                    <ArrowRight size={20} aria-hidden="true" />
                  </a>
                </ProductDetailLink>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

PairCards.propTypes = {
  cards: PropTypes.array.isRequired,
  getProduct: PropTypes.func.isRequired,
};

export const ProductTestimonial = ({ testimonial }) => (
  <section id={SECTION_IDS.testimonial} className={styles.testimonial}>
    <div className={classNames(styles.container, styles.testimonialRow)}>
      <figure className={styles.testimonialQuote}>
        <span className={styles.quoteMark} aria-hidden="true">
          “
        </span>
        <blockquote
          className={classNames(styles.quoteText, {
            [styles.quoteLong]: testimonial.quote.length > LONG_QUOTE_CHARS,
          })}
        >
          {testimonial.quote}
        </blockquote>
        <figcaption className={styles.quoteAuthor}>{testimonial.author}</figcaption>
      </figure>
      {testimonial.imageKey && (
        <div className={styles.testimonialPhoto}>
          <CloudinaryPicture
            prefix={IMAGE_PREFIX}
            imageKey={testimonial.imageKey}
            widths={IMAGE_WIDTHS}
            sizes="(min-width: 992px) 460px, calc(100vw - 32px)"
            loading="lazy"
          />
        </div>
      )}
    </div>
  </section>
);

ProductTestimonial.propTypes = {
  testimonial: PropTypes.object.isRequired,
};

export const ProductFact = ({ fact }) => (
  <section id={SECTION_IDS.fact} className={styles.testimonial}>
    <div className={classNames(styles.container, styles.testimonialRow)}>
      <figure className={styles.testimonialQuote}>
        <p className={styles.factEyebrow}>{fact.eyebrow}</p>
        <blockquote className={styles.factStatement}>{fact.statement}</blockquote>
        {fact.support && <p className={styles.factSupport}>{fact.support}</p>}
        <figcaption className={classNames(styles.quoteAuthor, styles.factCitation)}>
          {fact.citation}
        </figcaption>
      </figure>
      {fact.imageKey && (
        <div className={styles.testimonialPhoto}>
          <CloudinaryPicture
            prefix={IMAGE_PREFIX}
            imageKey={fact.imageKey}
            widths={IMAGE_WIDTHS}
            sizes="(min-width: 992px) 460px, calc(100vw - 32px)"
            loading="lazy"
          />
        </div>
      )}
    </div>
  </section>
);

ProductFact.propTypes = {
  fact: PropTypes.object.isRequired,
};

// Page renders are decorative, the link label next to them names the page
const PageImage = ({ imageKey, sizes, className }) => (
  <CloudinaryPicture
    imageKey={imageKey}
    widths={WIDTH_LADDERS.landing}
    sizes={sizes}
    alt=""
    className={className}
    loading="lazy"
  />
);

PageImage.propTypes = {
  imageKey: PropTypes.string.isRequired,
  sizes: PropTypes.string.isRequired,
  className: PropTypes.string,
};

// Three small rotated pages, the back one first
const PageStack = ({ imageKeys, className }) => (
  <span className={classNames(styles.pageStack, className)} aria-hidden="true">
    {imageKeys.slice(0, 3).map((imageKey) => (
      <PageImage key={imageKey} imageKey={imageKey} sizes="120px" />
    ))}
  </span>
);

PageStack.propTypes = {
  imageKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  className: PropTypes.string,
};

/** Buy box link to the page types page, so the pages are reachable above the fold. */
export const InsideBookTeaser = ({ insideBook, onClick }) => (
  <Link href={insideBook.url} className={styles.insideTeaser} onClick={onClick}>
    <PageStack imageKeys={insideBook.pages.map(({ imageKey }) => imageKey).reverse()} />
    <span className={styles.insideTeaserText}>
      <span className={styles.insideTeaserTitle}>{insideBook.teaser.title}</span>
      <span className={styles.insideTeaserBody}>{insideBook.teaser.body}</span>
    </span>
    <ArrowRight size={20} aria-hidden="true" className={styles.insideTeaserArrow} />
  </Link>
);

InsideBookTeaser.propTypes = {
  insideBook: PropTypes.object.isRequired,
  onClick: PropTypes.func,
};

export const InsideBook = ({ insideBook, onClick }) => {
  const track = (target) => () => onClick(target);

  return (
    <section className={styles.inside} aria-labelledby="inside-book-title">
      <div className={styles.container}>
        <div className={styles.insideHead}>
          <div className={styles.sectionHead}>
            <p className={styles.eyebrow}>{insideBook.eyebrow}</p>
            <h2 id="inside-book-title" className={styles.insideTitle}>
              {insideBook.title}
            </h2>
            <p className={styles.insideIntro}>{insideBook.intro}</p>
          </div>
          <Link
            href={insideBook.url}
            className={classNames(styles.insideButton, styles.desktopOnly)}
            onClick={track('button')}
          >
            {insideBook.linkLabel}
            <ArrowRight size={20} aria-hidden="true" />
          </Link>
        </div>

        <ul className={styles.insidePages}>
          {insideBook.pages.map((page) => (
            <li key={page.title}>
              <Link href={insideBook.url} className={styles.insidePage} onClick={track('page')}>
                <PageImage
                  imageKey={page.imageKey}
                  sizes="(min-width: 992px) 300px, 260px"
                  className={styles.insidePageImage}
                />
                <span className={styles.insidePageLabel}>{page.title}</span>
              </Link>
            </li>
          ))}
          <li>
            <Link href={insideBook.url} className={styles.insidePage} onClick={track('more')}>
              <span className={styles.insideMoreTile}>
                <PageStack imageKeys={insideBook.more.imageKeys} className={styles.pageStackLarge} />
                <span className={styles.insideMoreTitle}>{insideBook.more.title}</span>
              </span>
              <span className={classNames(styles.insidePageLabel, styles.insideMoreLabel)}>
                {insideBook.more.body}
              </span>
            </Link>
          </li>
        </ul>

        <Link
          href={insideBook.url}
          className={classNames(styles.insideButton, styles.phoneOnly)}
          onClick={track('button')}
        >
          {insideBook.linkLabel}
          <ArrowRight size={20} aria-hidden="true" />
        </Link>
      </div>
    </section>
  );
};

InsideBook.propTypes = {
  insideBook: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired,
};

export const ProductFaq = ({ caption, intro, faq }) => (
  <section id={SECTION_IDS.faq} className={styles.faq}>
    <div className={classNames(styles.container, styles.faqLayout)}>
      <div className={styles.faqHead}>
        <h2 className={styles.faqTitle}>{`Questions about ${caption}`}</h2>
        {intro && <p className={styles.faqIntro}>{intro}</p>}
      </div>

      <div className={styles.faqList}>
        <FaqAccordion
          items={faq.map(({ caption: question, description }) => ({
            question,
            answer: description,
          }))}
        />
      </div>

      <div className={styles.contactPanel}>
        <h3 className={styles.contactTitle}>Still have a question?</h3>
        <p className={styles.contactText}>Email our team. We are happy to help.</p>
        <a href={`mailto:${SUPPORT_EMAIL}`} className={styles.contactEmail}>
          {SUPPORT_EMAIL}
        </a>
      </div>
    </div>
  </section>
);

ProductFaq.propTypes = {
  caption: PropTypes.string.isRequired,
  intro: PropTypes.string,
  faq: PropTypes.array.isRequired,
};

export const RelatedProducts = ({ title, products }) => (
  <section id={SECTION_IDS.related} className={styles.related}>
    <div className={styles.container}>
      {title ? (
        <SectionHead eyebrow="You might like" title={title} />
      ) : (
        <SectionHead title="You might like" />
      )}
      <ul className={styles.relatedRow}>
        {products.map((product) => {
          const price = formatPrice(product.prices[0], product.currency);
          return (
            <li key={product.id}>
              <ProductDetailLink productId={product.id}>
                <a className={styles.relatedCard}>
                  <div className={styles.relatedPhoto}>
                    <CloudinaryPicture
                      prefix={IMAGE_PREFIX}
                      imageKey={productCoverImage(product)?.key}
                      widths={IMAGE_WIDTHS}
                      sizes="(min-width: 992px) 404px, 280px"
                      loading="lazy"
                    />
                  </div>
                  <div className={styles.relatedBody}>
                    <h3 className={styles.cardTitle}>{product.name}</h3>
                    {price && (
                      <p className={styles.relatedPrice}>
                        {product.prices.length > 1 ? `Starting at ${price}` : price}
                      </p>
                    )}
                    <span className={styles.relatedLink}>
                      View product
                      <ArrowRight size={20} aria-hidden="true" />
                    </span>
                  </div>
                </a>
              </ProductDetailLink>
            </li>
          );
        })}
      </ul>
    </div>
  </section>
);

RelatedProducts.propTypes = {
  title: PropTypes.string,
  products: PropTypes.array.isRequired,
};

export const SeoCopy = ({ html }) => (
  <section className={styles.seoCopy}>
    <div className={styles.container}>
      <HtmlContent html={html} className={styles.seoColumn} />
    </div>
  </section>
);

SeoCopy.propTypes = {
  html: PropTypes.string.isRequired,
};
